import { useEffect, useState } from "react";
import { api } from "../api/client";
import { DashboardData, TaskItem, TasksResponse } from "../types";
import { TabKey } from "../components/TabBar";
import {
  EmptyState,
  ErrorBox,
  SkeletonRows,
  StatusPill,
} from "../components/States";
import { PullToRefresh } from "../components/PullToRefresh";
import { fmtMoney } from "../format";

const KIND_ICON: Record<string, string> = {
  crypto_tx: "📥",
  exchange_pending: "🔁",
  awaiting_rate: "⏳",
  payable: "📒",
  payment_confirm: "✅",
};

const SECTION_LABEL: Record<string, string> = {
  fx: "Перейти в «Экспорт»",
  payments: "Перейти в «Платежи»",
};

export function TasksPage({
  onNavigate,
}: {
  onNavigate: (t: TabKey) => void;
}) {
  const [data, setData] = useState<TasksResponse | null>(null);
  const [dash, setDash] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const [t, d] = await Promise.all([
        api.get<TasksResponse>("/api/tasks"),
        api.get<DashboardData>("/api/tasks/dashboard"),
      ]);
      setData(t);
      setDash(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-3">
        <h1 className="text-xl font-semibold">✅ Мои задачи</h1>

        {error && <ErrorBox text={error} />}

      {dash && (
        <div className="grid grid-cols-2 gap-2">
          <KpiTile
            label="Открытых задач"
            value={dash.open_tasks}
            accent={dash.open_tasks > 0}
          />
          {dash.show_fx && (
            <>
              <KpiTile
                label="Обмены в ожидании"
                value={dash.pending_exchanges}
                accent={dash.pending_exchanges > 0}
              />
              <KpiTile
                label="Открытых долгов"
                value={dash.open_provider_debts}
                accent={dash.open_provider_debts > 0}
              />
              <KpiTile
                label="Крипто-пул, USDT"
                value={fmtMoney(dash.crypto_pool_usdt)}
              />
            </>
          )}
        </div>
      )}

        {data === null ? (
          <SkeletonRows rows={4} />
        ) : data.tasks.length === 0 ? (
          <EmptyState icon="🎉" text="Незакрытых задач нет." />
        ) : (
          <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
            {data.tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                open={openId === task.id}
                onToggle={() =>
                  setOpenId(openId === task.id ? null : task.id)
                }
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}

function KpiTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
}) {
  return (
    <div className="bg-tg-sectionBg rounded-xl p-3">
      <div
        className={
          "text-2xl font-bold tabular-nums " +
          (accent ? "text-tg-accentText" : "")
        }
      >
        {value}
      </div>
      <div className="text-xs text-tg-hint mt-0.5">{label}</div>
    </div>
  );
}

function TaskRow({
  task,
  open,
  onToggle,
  onNavigate,
}: {
  task: TaskItem;
  open: boolean;
  onToggle: () => void;
  onNavigate: (t: TabKey) => void;
}) {
  const high = task.severity === "high";
  return (
    <div
      onClick={onToggle}
      className="px-4 py-3 text-sm cursor-pointer"
    >
      <div className="flex items-baseline gap-2">
        <span>{KIND_ICON[task.kind] || "•"}</span>
        <span className="font-medium flex-1 min-w-0">{task.title}</span>
        {high && <StatusPill tone="danger">срочно</StatusPill>}
      </div>
      {open && (
        <div className="mt-2 pt-2 border-t border-tg-secondaryBg space-y-2">
          <div className="text-xs text-tg-hint">{task.detail}</div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(task.section as TabKey);
            }}
            className="w-full px-3 py-2 rounded-lg bg-tg-button text-tg-buttonText text-sm"
          >
            {SECTION_LABEL[task.section] || "Перейти к выполнению"}
          </button>
        </div>
      )}
      {!open && (
        <div className="text-xs text-tg-link mt-0.5">▼ подробнее</div>
      )}
    </div>
  );
}
